import React from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Loader2, Globe, Wrench, CreditCard, LifeBuoy, TrendingUp, LogOut, ArrowRight } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import { Container, Button } from "../components/site/ui";
import { Reveal } from "../components/site/Reveal";
import { useAuth } from "../context/AuthContext";
import { GROWTH_PATH } from "../data/site";

const SECTIONS = [
  { icon: Globe, title: "Website", desc: "Your live site, drafts, and launch status.", soon: true },
  { icon: Wrench, title: "Services", desc: "Maintenance, hosting, SEO and add-ons.", soon: true },
  { icon: CreditCard, title: "Billing", desc: "Invoices and payments.", action: { label: "Pay My Bill", to: "/pay-my-bill" } },
  { icon: LifeBuoy, title: "Support", desc: "Get help from a real person.", action: { label: "Contact us", to: "/get-started" } },
  { icon: TrendingUp, title: "Growth", desc: "SEO, marketing and business tools.", soon: true },
];

export default function ClientDashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();

  if (checking) {
    return <SiteShell title="Client Area — GoBuildWeb"><Container className="grid min-h-[60vh] place-items-center py-40"><Loader2 className="animate-spin text-gold-500" size={32} /></Container></SiteShell>;
  }
  if (!user) {
    return <Navigate to="/client-login" replace />;
  }

  const doLogout = () => { logout(); navigate("/client-login", { replace: true }); };
  const firstName = (user.name || user.email || "there").split(" ")[0];

  return (
    <SiteShell title="Client Area — GoBuildWeb" description="Your GoBuildWeb client area." testId="client-dashboard-page">
      <section className="pt-[72px]">
        <Container className="py-16 md:py-20">
          <Reveal>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500">Client Area</p>
                <h1 className="mt-2 font-display text-4xl font-bold text-white">Welcome, {firstName}</h1>
                <p className="mt-2 text-zinc-400">Manage your website, services, and billing in one place.</p>
              </div>
              <button onClick={doLogout} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-sm font-semibold text-white transition hover:border-gold-500 hover:text-gold-400" data-testid="client-logout"><LogOut size={15} /> Log out</button>
            </div>
          </Reveal>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid={`client-section-${s.title.toLowerCase()}`}>
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-gold-500/10 text-gold-400"><s.icon size={20} /></span>
                  <h3 className="mt-4 font-display text-xl font-bold text-white">{s.title}</h3>
                  <p className="mt-1.5 flex-1 text-sm text-zinc-400">{s.desc}</p>
                  {s.action ? (
                    <Link to={s.action.to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-400 hover:text-gold-300">{s.action.label} <ArrowRight size={14} /></Link>
                  ) : (
                    <span className="mt-4 inline-block rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-zinc-500">Coming soon</span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-7">
              <h3 className="font-display text-xl font-bold text-white">Your growth path</h3>
              <p className="mt-1 text-sm text-zinc-400">We grow with you — from your first website to a full digital system.</p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {GROWTH_PATH.map((step, i) => (
                  <React.Fragment key={step}>
                    <span className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-sm text-zinc-200">{step}</span>
                    {i < GROWTH_PATH.length - 1 && <ArrowRight size={14} className="text-gold-500/60" />}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-6"><Button to="/pay-my-bill" variant="secondary" icon={false} data-testid="client-pay-bill">Pay My Bill</Button></div>
            </div>
          </Reveal>
        </Container>
      </section>
    </SiteShell>
  );
}
